import * as React from 'react';
import {
  ProgramsState,
  Schedule,
  Program,
  LevelsState,
  LevelsResponseData,
  Level
} from './Types';
import Calendar from './CalendarComponent';
import { Event } from '../calendar/calendar.event.types';
import { getLocalMoment } from '../util/date-util';
import { AuthState } from '../auth/auth.types';
import { AdminState } from '../admin/admin.types';
import { isAdminAccount, isImpersonating } from '../admin/Util';
import { isProgramDisabled } from '../util/permission.util';
import { isAuthTokenExpired } from '../commons/AuthTokenValidator';
import CommonFeatures from '../commons/CommonFeatures';
import { MembersResponseData } from '../members/members.types';
import { isMemberDisabled } from '../util/permission.util';
import { BusinessState } from 'src/business/business.types';

const DEFAULT_COLOR = '#0b9cd5';

type Props = {
  readonly authState: AuthState
  readonly adminState: AdminState
  readonly programsState: ProgramsState
  readonly levelsState: LevelsState
  readonly businessState: BusinessState
  readonly getPrograms: () => Promise<Response>
  readonly getLevels: () => Promise<LevelsResponseData>
  readonly toDashboard: () => void
  readonly toAdminDashboard: () => void
  readonly getMembers: () => Promise<MembersResponseData>
};

type States = {
  readonly programs: Array<Event>
  readonly levels: Array<Level>
  readonly loadedPrograms: boolean
  readonly loadedLevels: boolean
};

class ProgramsComponent extends React.Component<Props, States> {
  
  constructor(props: Props) {
    super(props);
    
    this.state = {
      programs: [],
      levels: [],
      loadedPrograms: false,
      loadedLevels: false
    };

    this.createEventToDisplay = this.createEventToDisplay.bind(this);
    this.createEventsToDisplay = this.createEventsToDisplay.bind(this);
    this.openProgramsModalHandler = this.openProgramsModalHandler.bind(this);
  }

  componentDidMount() {
    const { authState, getPrograms, toAdminDashboard, adminState, toDashboard, getLevels } = this.props;
    const { loadedPrograms } = this.state;

    if (isAdminAccount(authState) && !isImpersonating(adminState)) {
      return toAdminDashboard();
    }

    if (!isMemberDisabled(authState, adminState) && !isAuthTokenExpired()) {
      this.props.getMembers();
    }

    if (!isProgramDisabled(authState, adminState) && !isAuthTokenExpired() && !loadedPrograms) {
      getPrograms()
      .then(response => {
        switch (this.props.programsState.statusCode) {
          case 200:
            return this.setState({
              loadedPrograms: true,
              programs: this.createEventsToDisplay(this.props.programsState.programs)
            });
          case 403:
            return toDashboard();
          default:
            return;
        }
      });
      getLevels();
    } else {
      return toDashboard();
    }
  }

  componentWillReceiveProps(nextProps: Props) {
    if (
      this.props !== nextProps &&
      !isAuthTokenExpired() &&
      nextProps.authState.statusCode === 200
    ) {

      switch (nextProps.programsState.statusCode) {
        case 200:
          this.setState({
            programs: this.createEventsToDisplay(nextProps.programsState.programs)
          });
          break;
        case 0:
          return !this.state.loadedPrograms ?
            this.setState({loadedPrograms: true}, nextProps.getPrograms) :
            this.setState({loadedPrograms: false});
        default:
          break;
      }

      switch (nextProps.levelsState.statusCode) {
        case 200:
          this.setState({
            levels: nextProps.levelsState.levels
          });
          break;
        case 0:
          return !this.state.loadedLevels ?
            this.setState({loadedLevels: true}, nextProps.getLevels) :
            this.setState({loadedLevels: false});
        default:
          break;
      }
    }
  }

  render() {
    const { authState, adminState } = this.props;

    return(
      <CommonFeatures
        authState={this.props.authState}
        businessState={this.props.businessState}
      >
        <div className="page-header-row row mt-2 mb-2">
          <div className="col-12"><h3>Programs</h3></div>
        </div>
        <div className="row mb-4">
          <div className="col-12">
            <Calendar
              events={this.state.programs}
              openProgramsModalHandler={this.openProgramsModalHandler}
              authState={authState}
              adminState={adminState}
            />
          </div>
        </div>
      </CommonFeatures>
    );
  }

  private createEventsToDisplay = (programs: Array<Program>): Array<Event> => {
      const newPrograms: Array<Array<Event>> = programs
        .map((program: Program) => this.createEventToDisplay(program));
        
      return [].concat.apply([], newPrograms);
  }

  private createEventToDisplay = (program: Program): Array<Event> => {
    return program.schedules
      .map((schedule: Schedule) => {
        return {
          id: program.id,
          title: program.name,
          start: schedule.startDate ? getLocalMoment(new Date(schedule.startDate)).toDate() : undefined,
          end: schedule.endDate ? getLocalMoment(new Date(schedule.endDate)).toDate() : undefined,
          backgroundColor: program.colorValue ? program.colorValue : DEFAULT_COLOR
        };
      });
  }

  private openProgramsModalHandler() {
    const { getLevels } = this.props;

    if (getLevels) {
      getLevels()
      .then((levelsResponseData: LevelsResponseData) => {
        const { levelsState } = this.props;
        switch (levelsState.statusCode) {
          case 200:
            this.setState({
              levels: levelsState.levels
            });
            break;
          default:
            break;
        }
      });
    }
  }
}

export default ProgramsComponent;



// WEBPACK FOOTER //
// ./src/programs/ProgramsComponent.tsx